import classNames from 'classnames';
import _ from 'lodash';
import { t } from 'ttag';
import './style.scss';
import type { DeviceStatusFormatterProps } from './deviceStatusFormatter.types';
import { DEVICE_STATUS } from '../../lib/deviceConstants';

/**
 * Status cell showing a device as offline, online or still resolving. Renders a gray circle with an Offline label when the status is offline, a green circle with an Online label when no event notification matches the device, and a double-bounce spinner otherwise.
 *
 * @param deviceId - Device id used to match event notifications.
 * @param eventsNoti - Event notifications used to detect an active event.
 * @param status - Current device status.
 * @returns The device status cell element.
 */
const DeviceStatusFormatter = ({ deviceId, eventsNoti, status }: DeviceStatusFormatterProps) => {
  /**
   * Locates a device inside the event notification list to tell whether it has an active event. Matches on deviceId and returns the position found, so a negative result means the device has no notification.
   *
   * @param deviceId - Device id to look for.
   * @param eventsNoti - Event notification records to search.
   * @returns Index of the matching notification, or -1 when there is none.
   */
  const checkDeviceStatus = (deviceId: any, eventsNoti: any) =>
    _.findIndex(eventsNoti, (x: any) => x.deviceId === deviceId);

  const isGrayStatusCircle = status === DEVICE_STATUS.OFFLINE;
  const isGreenStatusCircle = checkDeviceStatus(deviceId, eventsNoti) < 0;

  return isGrayStatusCircle || isGreenStatusCircle ? (
    <div className="device-status-container">
      <div className="status-circle">
        <div
          className={classNames(
            'status-circle__item',
            isGrayStatusCircle ? '--gray' : isGreenStatusCircle ? '--green' : '--yellow',
          )}
        />
      </div>
      <div className={isGrayStatusCircle ? undefined : 'green-text'}>
        {isGrayStatusCircle ? t`Offline` : t`Online`}
      </div>
    </div>
  ) : (
    <div className="sk-double-bounce">
      <div className="sk-child sk-double-bounce1" />
      <div className="sk-child sk-double-bounce2" />
    </div>
  );
};

export default DeviceStatusFormatter;
